<template>
  <div>
    <div>
      <v-breadcrumbs
        class="px-0"
        :items="breadcrumbItems"
      />
    </div>
    <page-title :value="pageTitle" />

    <div class="my-3 d-flex align-center flex-wrap" style="gap: 0.5rem;">
      <v-btn
        color="primary"
        variant="flat"
        :disabled="isFetching || isProcessing"
        :loading="isProcessing"
        @click="openBulkGenerate"
      >
        <v-icon start>mdi-auto-fix</v-icon>
        {{ isProcessing ? "生成中..." : "一括生成" }}
      </v-btn>
      <v-btn
        color="primary"
        variant="outlined"
        :disabled="isFetching || draftCount === 0 || isPublishing"
        :loading="isPublishing"
        @click="bulkPublish"
      >
        <v-icon start>mdi-publish</v-icon>
        下書きをすべて公開 ({{ draftCount }})
      </v-btn>
      <v-spacer />
      <v-btn
        variant="outlined"
        :disabled="isFetching || isExporting"
        :loading="isExporting"
        @click="exportCsv"
      >
        <v-icon start>mdi-file-table-outline</v-icon>
        CSV出力
      </v-btn>
    </div>

    <v-alert
      v-if="activeBatch && activeBatch.status === 'processing'"
      variant="outlined"
      color="primary"
      icon="mdi-progress-clock"
      class="my-2"
      density="compact"
    >
      <div class="d-flex align-center flex-wrap" style="gap: 1rem;">
        <span class="text-body-2">
          バッチ {{ activeBatch.batchId }} を生成中... ({{ activeBatch.successCount }} / {{ activeBatch.totalCount }})
        </span>
        <v-progress-linear
          :model-value="batchProgressPercent"
          color="primary"
          height="8"
          rounded
          style="max-width: 240px;"
        />
      </div>
    </v-alert>

    <v-progress-linear v-if="isFetching" indeterminate color="primary" />

    <div v-if="quarterDetail">
      <div
        v-for="cls in quarterDetail.classes"
        :key="cls.classId"
        class="my-4"
      >
        <div class="d-flex align-center mb-1" style="gap: 0.5rem;">
          <span class="text-h6 font-weight-bold">{{ cls.className }}</span>
          <span class="text-caption text-grey">({{ cls.ageLabel }} / {{ cls.children.length }}名)</span>
        </div>
        <v-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th style="width: 200px;">園児名</th>
                <th style="width: 120px;">ステータス</th>
                <th style="width: 110px;">生成回数</th>
                <th style="width: 120px;">達成項目数</th>
                <th style="width: 120px;">経過記録数</th>
                <th>最終生成</th>
                <th>最終公開</th>
                <th class="text-right" style="width: 200px;">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in cls.children" :key="c.childId">
                <td>{{ c.lastName }} {{ c.firstName }}</td>
                <td>
                  <ai-report-status-chip :status="c.status" />
                </td>
                <td>
                  <span class="text-body-2">{{ c.generationCount }} 回</span>
                </td>
                <td>
                  <span class="text-body-2">{{ c.achievedItemCount }} 個</span>
                </td>
                <td>
                  <span class="text-body-2">{{ c.progressRecordCount }} 件</span>
                </td>
                <td>
                  <div class="text-body-2">{{ formatDateTime(c.lastGeneratedAt) }}</div>
                  <div v-if="c.lastGeneratedBy" class="text-caption text-grey">
                    by {{ c.lastGeneratedBy }}
                  </div>
                </td>
                <td>
                  <div class="text-body-2">{{ formatDateTime(c.lastPublishedAt) }}</div>
                  <div v-if="c.lastPublishedBy" class="text-caption text-grey">
                    by {{ c.lastPublishedBy }}
                  </div>
                </td>
                <td class="text-right">
                  <v-btn
                    color="primary"
                    variant="outlined"
                    size="small"
                    :to="childPath(c.childId)"
                  >
                    レポートを開く
                  </v-btn>
                </td>
              </tr>
              <tr v-if="cls.children.length === 0">
                <td colspan="8" class="text-center text-grey">
                  対象クォーターに在籍する園児がいません。
                </td>
              </tr>
            </tbody>
          </template>
        </v-table>
      </div>

      <v-alert
        v-if="quarterDetail.classes.length === 0"
        type="info"
        variant="outlined"
      >
        対象クォーターのクラスがありません。
      </v-alert>
    </div>

    <ai-report-bulk-generate-dialog
      v-model="bulkDialogOpen"
      :business-year="year"
      :quarter="quarter"
      :months-label="quarterDetail ? quarterDetail.monthsLabel : ''"
      :batch="activeBatch"
      :is-starting="isStartingBulk"
      @start="startBulkGenerate"
      @close="onBulkDialogClose"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import PageTitle from "@/components/atoms/PageTitle.vue";
import AiReportStatusChip from "@/components/molecules/AiReportStatusChip.vue";
import AiReportBulkGenerateDialog from "@/components/organisms/ai-report/AiReportBulkGenerateDialog.vue";
import { aiReportQuarterModule } from "@/store/viewModules/ai-report/aiReportQuarterModule";
import type { AiReportQuarter } from "@/api/ApiAiReport";

interface DataType {
  year: number;
  quarter: AiReportQuarter;
  bulkDialogOpen: boolean;
  isStartingBulk: boolean;
  isPublishing: boolean;
  isExporting: boolean;
  pollTimer: number | null;
}

export default defineComponent({
  components: {
    PageTitle,
    AiReportStatusChip,
    AiReportBulkGenerateDialog,
  },
  data: (): DataType => ({
    year: 0,
    quarter: 1 as AiReportQuarter,
    bulkDialogOpen: false,
    isStartingBulk: false,
    isPublishing: false,
    isExporting: false,
    pollTimer: null,
  }),
  computed: {
    quarterDetail() {
      return aiReportQuarterModule.quarterDetail;
    },
    isFetching(): boolean {
      return aiReportQuarterModule.isFetching;
    },
    activeBatch() {
      return aiReportQuarterModule.activeBatch;
    },
    pageTitle(): string {
      if (!this.quarterDetail) {
        return `VEVOレポート - 第${this.quarter}クォーター (${this.year}年度)`;
      }
      return `VEVOレポート - ${this.quarterDetail.monthsLabel} (${this.year}年度・第${this.quarter}クォーター)`;
    },
    breadcrumbItems(): Array<{ title: string; href?: string; color?: string }> {
      const monthsLabel = this.quarterDetail
        ? this.quarterDetail.monthsLabel
        : `第${this.quarter}クォーター`;
      return [
        { title: "VEVOレポート", href: "/ai-report", color: "primary" },
        { title: `${this.year}年度 ${monthsLabel}` },
      ];
    },
    draftCount(): number {
      if (!this.quarterDetail) return 0;
      return this.quarterDetail.classes.reduce(
        (n, cls) => n + cls.children.filter((c) => c.status === "draft").length,
        0,
      );
    },
    batchProgressPercent(): number {
      const b = this.activeBatch;
      if (!b || b.totalCount === 0) return 0;
      return Math.floor((b.successCount / b.totalCount) * 100);
    },
    isProcessing(): boolean {
      return !!this.activeBatch && this.activeBatch.status === "processing";
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  mounted() {
    const year = parseInt(this.$route.params.businessYear as string, 10);
    const quarter = parseInt(this.$route.params.quarter as string, 10) as AiReportQuarter;
    if (!Number.isNaN(year)) this.year = year;
    if ([1, 2, 3, 4].includes(quarter)) this.quarter = quarter;
    this.loadQuarter();
  },
  methods: {
    async loadQuarter() {
      await aiReportQuarterModule.loadQuarter({
        businessYear: this.year,
        quarter: this.quarter,
      });
      // リロード時など、進行中バッチがあれば進捗表示とポーリングを復元する。
      // 生成はサーバ側(Step Functions)で非同期に走っているため、画面を離れても継続する。
      const latest = this.quarterDetail?.latestBatch ?? null;
      if (latest && latest.status === "processing") {
        aiReportQuarterModule.setActiveBatch(latest);
        if (this.pollTimer === null) {
          this.startPolling(latest.batchId);
        }
      }
    },
    openBulkGenerate() {
      aiReportQuarterModule.setActiveBatch(null);
      this.bulkDialogOpen = true;
    },
    async startBulkGenerate() {
      this.isStartingBulk = true;
      try {
        const batch = await aiReportQuarterModule.bulkGenerate({
          businessYear: this.year,
          quarter: this.quarter,
        });
        if (batch) {
          this.startPolling(batch.batchId);
        }
      } finally {
        this.isStartingBulk = false;
      }
    },
    startPolling(batchId: number) {
      this.stopPolling();
      this.pollTimer = window.setInterval(async () => {
        const batch = await aiReportQuarterModule.pollBatch({ batchId });
        if (batch && (batch.status === "completed" || batch.status === "failed")) {
          this.stopPolling();
          await this.loadQuarter();
        }
      }, 1500);
    },
    stopPolling() {
      if (this.pollTimer !== null) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    onBulkDialogClose() {
      this.stopPolling();
    },
    async bulkPublish() {
      if (this.draftCount === 0) return;
      const ok = window.confirm(
        `下書き状態の ${this.draftCount} 名のレポートをすべて公開します。よろしいですか？\n保護者に通知が送信されます。`,
      );
      if (!ok) return;
      this.isPublishing = true;
      try {
        const result = await aiReportQuarterModule.bulkPublish({
          businessYear: this.year,
          quarter: this.quarter,
        });
        if (result) {
          alert(`${result.publishedCount} 名のレポートを公開しました。`);
          await this.loadQuarter();
        }
      } finally {
        this.isPublishing = false;
      }
    },
    async exportCsv() {
      this.isExporting = true;
      try {
        const response = await aiReportQuarterModule.exportCsv({
          businessYear: this.year,
          quarter: this.quarter,
        });
        if (response) {
          alert(
            "CSV出力を開始しました。\nダウンロード一覧画面からご確認ください。\n※完了まで1-2分程度かかる場合があります。",
          );
        } else {
          alert("CSV出力に失敗しました。");
        }
      } catch {
        alert("CSV出力に失敗しました。");
      } finally {
        this.isExporting = false;
      }
    },
    childPath(childId: number): string {
      return `/ai-report/detail/${this.year}/${this.quarter}/${childId}`;
    },
    formatDateTime(value: string | null): string {
      if (!value) return "—";
      return dayjs(value).format("YYYY/MM/DD HH:mm");
    },
  },
});
</script>
